import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { salaryService } from '../services/api';
import {
  Card, Badge, EmptyState, Table, Pagination, Modal, Button,
} from '../components/common/UI';
import { getStatusConfig, formatDateTimeUz } from '../utils/format';
import '../components/common/styles.css';

export default function UploadLogsPage() {
  const [page, setPage] = useState(1);
  const [errorModal, setErrorModal] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['upload-logs', page],
    queryFn: () => salaryService.uploadLogs({ page }).then(r => r.data),
    keepPreviousData: true,
  });

  const columns = [
    {
      label: 'Fayl nomi', key: 'file_name',
      render: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>📄</span>
          <span style={{ fontSize: 13, fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.file_name}
          </span>
        </div>
      ),
    },
    {
      label: 'Oy', key: 'month',
      render: (row) => (
        <code style={{
          fontSize: 12, background: 'var(--sky-50)',
          padding: '3px 8px', borderRadius: 4, color: 'var(--sky-700)',
        }}>{row.month || '—'}</code>
      ),
    },
    {
      label: 'Holat', key: 'status',
      render: (row) => {
        const sc = getStatusConfig(row.status);
        return <Badge color={sc.color} bg={sc.bg}>{sc.label}</Badge>;
      },
    },
    {
      label: 'Natijalar', key: 'results',
      render: (row) => (
        <div style={{ display: 'flex', gap: 12, fontSize: 13 }}>
          <span style={{ color: 'var(--text-secondary)' }}>
            Jami: <strong>{row.total_rows}</strong>
          </span>
          <span style={{ color: 'var(--emerald-500)' }}>
            ✓ <strong>{row.success_rows}</strong>
          </span>
          {row.error_rows > 0 && (
            <span style={{ color: 'var(--rose-500)' }}>
              ✕ <strong>{row.error_rows}</strong>
            </span>
          )}
        </div>
      ),
    },
    {
      label: 'Kim yuklagan', key: 'uploaded_by_username',
      render: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{
            width: 26, height: 26, borderRadius: '50%',
            background: 'var(--sky-100)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, color: 'var(--sky-700)',
          }}>
            {(row.uploaded_by_username || 'A')[0].toUpperCase()}
          </div>
          <span style={{ fontSize: 13 }}>{row.uploaded_by_username || 'Admin'}</span>
        </div>
      ),
    },
    {
      label: 'Vaqt', key: 'uploaded_at',
      render: (row) => (
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {formatDateTimeUz(row.uploaded_at)}
        </div>
      ),
    },
    {
      label: 'Xatolar', key: 'errors',
      render: (row) => row.error_rows > 0 ? (
        <Button
          variant="danger" size="sm"
          onClick={() => setErrorModal(row)}
        >
          {row.error_rows} xato →
        </Button>
      ) : (
        <span style={{ fontSize: 12, color: 'var(--emerald-500)' }}>✓ Xatosiz</span>
      ),
    },
  ];

  return (
    <div>
      {/* Summary */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
        {[
          { label: "Jami yuklashlar", value: data?.count ?? '—', icon: '📤', color: '#0ea5e9' },
          {
            label: "Muvaffaqiyatli",
            value: data?.results?.filter(r => r.status === 'success').length ?? '—',
            icon: '✅', color: '#10b981'
          },
          {
            label: "Qisman",
            value: data?.results?.filter(r => r.status === 'partial').length ?? '—',
            icon: '⚠️', color: '#f59e0b'
          },
          {
            label: "Muvaffaqiyatsiz",
            value: data?.results?.filter(r => r.status === 'failed').length ?? '—',
            icon: '❌', color: '#f43f5e'
          },
        ].map(({ label, value, icon, color }, i) => (
          <div key={label} className="stat-card fade-in" style={{
            animationDelay: `${i * 60}ms`, '--stat-accent': color + '22',
          }}>
            <div className="stat-icon" style={{ background: color + '18' }}>{icon}</div>
            <div className="stat-value">{value}</div>
            <div className="stat-label">{label}</div>
          </div>
        ))}
      </div>

      <Card>
        <div className="card-header">
          <span className="card-title">Excel yuklash tarixi</span>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Barcha yuklash operatsiyalari
          </span>
        </div>

        <Table
          columns={columns}
          data={data?.results}
          loading={isLoading}
          emptyState={
            <EmptyState
              icon="📋"
              title="Yuklash tarixi yo'q"
              description="Excel fayllar yuklanganda bu yerda ko'rinadi"
            />
          }
        />

        <Pagination
          count={data?.count || 0}
          page={page}
          onChange={setPage}
        />
      </Card>

      {/* Error detail modal */}
      <Modal
        isOpen={!!errorModal}
        onClose={() => setErrorModal(null)}
        title={`Xato tafsilotlari — ${errorModal?.file_name}`}
        width={600}
      >
        {errorModal && (
          <>
            <div style={{
              display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap',
            }}>
              <Badge color="var(--sky-600)" bg="var(--sky-100)">Jami: {errorModal.total_rows}</Badge>
              <Badge color="var(--emerald-500)" bg="var(--emerald-100)">✓ {errorModal.success_rows}</Badge>
              <Badge color="var(--rose-500)" bg="var(--rose-100)">✕ {errorModal.error_rows}</Badge>
            </div>

            <div style={{
              maxHeight: 400, overflowY: 'auto',
              border: '1px solid var(--border)', borderRadius: 'var(--radius)',
            }}>
              {(errorModal.error_log || []).map((err, i) => (
                <div key={i} style={{
                  padding: '10px 14px',
                  borderBottom: i < errorModal.error_log.length - 1 ? '1px solid var(--border)' : 'none',
                  display: 'flex', gap: 12, alignItems: 'flex-start',
                  background: i % 2 === 0 ? 'transparent' : 'var(--rose-100)',
                }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700,
                    background: 'var(--rose-100)', color: 'var(--rose-500)',
                    padding: '2px 7px', borderRadius: 4, flexShrink: 0,
                    fontFamily: 'monospace',
                  }}>
                    {err.row ? `Qator ${err.row}` : `#${i+1}`}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {err.jshshir && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>
                        JSHSHIR: <code>{err.jshshir}</code>
                        {err.name && ` — ${err.name}`}
                      </div>
                    )}
                    <div style={{ fontSize: 13, color: 'var(--rose-500)', fontWeight: 500 }}>
                      {err.error}
                    </div>
                  </div>
                </div>
              ))}

              {(!errorModal.error_log || errorModal.error_log.length === 0) && (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
                  Xato ma'lumotlari mavjud emas
                </div>
              )}
            </div>

            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setErrorModal(null)}>Yopish</Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
